import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Mail,
  Users,
  Calendar,
  Clock,
  CheckCircle,
  Send,
  Eye,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { EmailCampaign } from '../../services/emailCampaignService';
import { formatDate } from '../../lib/utils';

interface ViewEmailCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: EmailCampaign | null;
  onEdit?: (campaign: EmailCampaign) => void;
  onDelete?: (campaign: EmailCampaign) => void;
}

export function ViewEmailCampaignModal({ 
  isOpen, 
  onClose, 
  campaign,
  onEdit,
  onDelete
}: ViewEmailCampaignModalProps) {
  if (!campaign) return null;

  const handleClose = () => {
    onClose();
  };

  const getStatusConfig = (status: string) => {
    return {
      color: 'bg-green-100 text-green-800',
      label: 'Sent',
      icon: CheckCircle
    };
  };

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'newsletter':
        return {
          color: 'bg-blue-100 text-blue-800',
          label: 'Newsletter',
          icon: Mail
        };
      case 'promotional':
        return {
          color: 'bg-purple-100 text-purple-800',
          label: 'Promotional',
          icon: Users
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          label: type,
          icon: Mail
        };
    }
  };

  const statusConfig = getStatusConfig(campaign.status);
  const typeConfig = getTypeConfig(campaign.type);
  const StatusIcon = statusConfig.icon;
  const TypeIcon = typeConfig.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Mail className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Email Campaign Details
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      View campaign information and content
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0 rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)] bg-gradient-to-b from-gray-50/50 to-white">
              <div className="space-y-8">
                {/* Campaign Info Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-green/5 px-6 py-4 border-b border-jamaica-green/20">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-jamaica-green" />
                      <h3 className="text-lg font-semibold text-gray-900">Campaign Information</h3>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Subject and Status */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-600">Subject Line</label>
                        <p className="text-lg font-semibold text-gray-900 mt-1">{campaign.subject}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={typeConfig.color}>
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </div>

                    {/* Campaign Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-600">Campaign Name</label>
                      <p className="text-base text-gray-900 mt-1">{campaign.name}</p>
                    </div>

                    {/* Recipients and Date Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Recipients</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-base font-semibold text-gray-900">
                            {campaign.recipients.toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-500">
                            {campaign.type === 'newsletter' ? 'newsletter subscribers' : 'promotional subscribers'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Sent Date</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-base text-gray-900">
                            {formatDate(campaign.sentDate)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Template Info */}
                    {campaign.template && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Template</label>
                        <p className="text-base text-gray-900 mt-1 capitalize">{campaign.template.replace(/-/g, ' ')}</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Email Content Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-blue-50 to-blue-25 px-6 py-4 border-b border-blue-100">
                    <div className="flex items-center gap-2">
                      <Eye className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Email Content</h3>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="prose prose-sm max-w-none">
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                          {campaign.content}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Metadata Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-gray-50 to-gray-25 px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-gray-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Campaign Metadata</h3>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {formatDate(campaign.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-600">Last Updated</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {formatDate(campaign.updatedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50">
              <div className="flex items-center justify-end gap-3">
                {onDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(campaign)}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Campaign
                  </Button>
                )}
                <Button onClick={handleClose} variant="default">
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 